import { ChangeEvent, memo, useCallback, useMemo, useState } from "react";
import { useStore } from "react-redux";
import { generatePath, useNavigate } from "react-router-dom";
import _ from "lodash";
import { Box, ImageSlider } from "../../../common/components";
import { CART_ACTION_ENUM } from "../../../common/reducers/cart";
import config from "../../../config";
import { createStyles, palette, spacing } from "../../../styles/theme";
import { ComboItems, ItemPricing, Size } from "../../../types";
import { ROUTES } from "../../../constant/common.constant";
import { useThemeBreakpoints } from "../../../utils/themeUtils";
import CartItemNameAndPrice from "./CartItemNameAndPrice";
import SizeInput from "../../../components/Sizes/SizeInput";
import { CalculatePriceModal } from "../../../components/CalculatePriceModal";
import { useCart } from "../../../utils/CartHooks";
import CartItemAction from "./CartItemAction";
import CartItemWarnings from "../Components/CartItemWarnings";
import CartItemSizeRecommendation from "./CartItemSizeRecommendation";

const styles = createStyles({
  sizeContainer: {
    display: "grid",
    gridTemplateColumns: {
      xs: `repeat(auto-fill,minmax(48px, 1fr))`,
      lg: `repeat(auto-fill,minmax(48px, 1fr))`,
    },
    columnGap: {
      xs: spacing.smallMobile,
      lg: spacing.smallDesktop,
    },
    rowGap: "24px",
  },
  generalInfo: {
    display: "flex",
    flexDirection: { xs: "column", lg: "row" },
    alignItems: "start",
    padding: { xs: spacing.wrapperMobile, lg: spacing.wrapperDesktop },
    gap: { xs: spacing.regularMobile, lg: spacing.regularDesktop },
  },
  imgContainer: {
    display: "flex",
    alignItems: "flex-start",
    mr: { xs: spacing.regularMobile, lg: spacing.regularDesktop },
    gap: { xs: spacing.mediumMobile, lg: undefined },
  },
  bundleHeader: {
    p: spacing.groupingMobile,
    borderRadius: "4px 4px 0 0",
    background: palette.grey.lightBackground,
  },
});

interface Props {
  item: ItemPricing;
  comboItems: ComboItems;
  onRemove: (id: string, productName: string) => void;
  isBundle: boolean;
}

const CartItemSizeSelectorContent = (props: Props) => {
  const {
    onRemove,
    item: {
      deprecatedDesign: deprecatedDesignFromCartItem,
      cartItemId,
      productUrls,
      productName,
      designId,
      sizes,
      projectId,
      teamId,
    },
    comboItems,
    isBundle,
  } = props;
  const navigate = useNavigate();

  const { isMobile } = useThemeBreakpoints();
  const [isPriceModalOpen, setIsPriceModalOpen] = useState(false);
  const { dispatch } = useStore();
  const { updateCartSizes } = useCart();
  const [isRecommendSizesOpen, setIsRecommendSizesOpen] = useState(false);

  /**
   * Because the onChange will most likely be throttled and
   * the call to update sizes will be followed by a get of updated items,
   * We must use a local size that will be updated on size change (for the user to get feedback)
   * and when the get items will return (thus sizes from props will be updated), the local values will be updated if the backend didn't update to what the customer asked.
   * That way what will be displayed is at one point what the customer wants it to be and after what the server has stored
   */
  const [sizesQty, setSizesQty] = useState(sizes);

  // eslint-disable-next-line
  const saveItems = useCallback(
    _.debounce(async (updatedSizes: Size[]) => {
      await updateCartSizes({
        sizes: updatedSizes,
        cartItemId,
      });

      dispatch({
        type: CART_ACTION_ENUM.SET_SAVING,
        payload: { isSaving: false },
      });
    }, 300),
    [cartItemId, dispatch, updateCartSizes]
  );

  const onSizeChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    size: Size
  ) => {
    const amount = Number(e.target.value) || 0;

    const newSizesQty = sizesQty.map((x) =>
      x.name === size.name ? { ...x, qty: amount } : x
    );
    setSizesQty(newSizesQty);

    dispatch({
      type: CART_ACTION_ENUM.UPDATE_ITEM_SIZES,
      payload: {
        id: cartItemId,
        size,
        amount,
      },
    });

    dispatch({
      type: CART_ACTION_ENUM.SET_SAVING,
      payload: { isSaving: true },
    });

    saveItems(newSizesQty);
  };

  const deprecatedDesign = useMemo(() => {
    return (
      (deprecatedDesignFromCartItem &&
        config.blockPaymentWhenDesignIsDeprecated) ||
      false
    );
  }, [deprecatedDesignFromCartItem]);

  return (
    <Box sx={styles.generalInfo}>
      <Box sx={styles.imgContainer}>
        <ImageSlider
          images={productUrls}
          containerWidth={isMobile ? "72px" : "150px"}
          containerHeight={isMobile ? "84px" : "176px"}
          onClick={() =>
            navigate(
              generatePath(ROUTES.STUDIO, {
                projectId,
                designId,
                teamId,
              })
            )
          }
        />
        {isMobile && (
          <Box maxWidth="100%" overflow="hidden" display="grid">
            <CartItemNameAndPrice
              deprecatedDesign={deprecatedDesign}
              item={props.item}
              comboItems={comboItems}
              isBundle={isBundle}
            />
            {!isBundle && (
              <CartItemWarnings
                deprecatedDesign={deprecatedDesign}
                item={props.item}
                comboItems={comboItems}
              />
            )}
            <CartItemAction
              onRecommendSizesClick={() =>
                setIsRecommendSizesOpen((prev) => !prev)
              }
              onRemove={() => onRemove(cartItemId, productName)}
              showRecommendSizes={!!props.item?.sizeRecommendations?.length}
              item={props.item}
            />
          </Box>
        )}
      </Box>
      <Box
        display="flex"
        flexDirection="column"
        width="100%"
        gap={{ xs: spacing.regularMobile, lg: spacing.regularDesktop }}
        sx={{
          overflow: "hidden",
        }}
      >
        {!isMobile && (
          <Box
            display="flex"
            flexDirection="column"
            gap={{ xs: spacing.subtitleMobile, lg: spacing.subtitleDesktop }}
          >
            <CartItemNameAndPrice
              deprecatedDesign={deprecatedDesign}
              item={props.item}
              comboItems={comboItems}
              isBundle={isBundle}
            />
            <CartItemWarnings
              deprecatedDesign={deprecatedDesign}
              item={props.item}
              comboItems={comboItems}
              forCartItem={true}
            />
            <CartItemAction
              onRemove={() => onRemove(cartItemId, productName)}
              onRecommendSizesClick={() =>
                setIsRecommendSizesOpen((prev) => !prev)
              }
              showRecommendSizes={!!props.item?.sizeRecommendations?.length}
              item={props.item}
            />
          </Box>
        )}
        <Box display="flex" sx={styles.sizeContainer}>
          {sizesQty?.map((size, index) => (
            <SizeInput
              key={`${size.name} ${index}`}
              itemSize={size}
              onSizeChange={onSizeChange}
            />
          ))}
        </Box>
        {isRecommendSizesOpen && (
          <Box>
            <CartItemSizeRecommendation
              item={props.item}
              baseQty={comboItems.quantity}
              comboItems={comboItems}
              onClose={() => setIsRecommendSizesOpen(false)}
            />
          </Box>
        )}
      </Box>
      {!isMobile && (
        <CalculatePriceModal
          open={isPriceModalOpen}
          onClose={() => setIsPriceModalOpen(false)}
          designId={designId}
          productName={productName}
          defaultQuantity={comboItems.quantity}
        />
      )}
    </Box>
  );
};

export default memo(CartItemSizeSelectorContent);
